import { ConnectedPosition, FlexibleConnectedPositionStrategy, Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  DoCheck,
  effect,
  ElementRef,
  forwardRef,
  inject,
  input,
  OnDestroy,
  OnInit,
  output,
  signal,
  TemplateRef,
  untracked,
  viewChild,
  ViewContainerRef,
  ViewEncapsulation,
} from '@angular/core';
import { FormControl, FormsModule, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, map, takeUntil } from 'rxjs/operators';
import { HxBaseControl } from '../../base';
import { HxInput } from '../input/input';
import { HxSelectPosition } from '../select/shared/select-types';
import { HxTagComponent } from '../tag/tag';
import { SearchItem, SearchTypeaheadLoadMoreEvent } from './search-typeahead.types';

/**
 * HxSearchTypeAhead Component
 *
 * A search component that enables users to search and filter items from a predefined list and add selected items as
 * removable tags. Features real-time search filtering with text highlighting, configurable minimum character threshold,
 * duplicate prevention, keyboard navigation, and comprehensive accessibility support (WCAG 2.1 AA).
 *
 * ## Selection State Management
 *
 * The component manages selection state using the `isSelected` property on items:
 *
 * - Add `isSelected: true` to pre-selected items in the `items` array
 * - Listen to `selectedItemsChanged` to get the selected items array
 * - Listen to `itemsChanged` event to get updated items with selection state
 * - Component automatically syncs selection state with items
 * - Selection state is maintained in the source data (items array)
 *
 * @example
 *   ```typescript
 *   import { Component, signal } from '@angular/core';
 *   import { HxSearchTypeAhead, type SearchItem } from '@complexcare/harmony/search-typeahead';
 *
 *   @Component({
 *   selector: 'app-tech-selector',
 *   standalone: true,
 *   imports: [HxSearchTypeAhead],
 *   template: `
 *   <hx-search-typeahead
 *   [items]="items()"
 *   (selectedItemsChanged)="onSelectedItemsChanged($event)"
 *   (itemsChanged)="onItemsChanged($event)"
 *   />
 *   `,
 *   })
 *   export class TechSelectorComponent {
 *   items = signal<SearchItem[]>([
 *   { id: 1, displayText: 'Angular', isSelected: true },
 *   { id: 2, displayText: 'React', isSelected: false },
 *   { id: 3, displayText: 'Vue', isSelected: false },
 *   ]);
 *
 *   onSelectedItemsChanged(selectedItems: SearchItem[]) {
 *   console.log(selectedItems);
 *   }
 *
 *   onItemsChanged(updatedItems: SearchItem[]) {
 *   this.items.set(updatedItems);
 *   // Selection state is now embedded in items with isSelected flags
 *   }
 *   }
 *   ```
 *
 * @see specs/037-search-typeahead/spec.md - Feature specification
 * @see specs/037-search-typeahead/plan.md - Implementation plan
 */
@Component({
  selector: 'hx-search-typeahead',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, HxInput, HxTagComponent, ScrollingModule],
  templateUrl: './search-typeahead.html',
  styleUrls: ['./search-typeahead.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  host: {
    '[class.hx-search-typeahead]': 'true',
    '[class.hx-search-typeahead--disabled]': 'disabledState()',
    '[class.hx-search-typeahead--readonly]': 'readonly()',
  },
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => HxSearchTypeAhead),
      multi: true,
    },
  ],
})
export class HxSearchTypeAhead extends HxBaseControl implements OnInit, OnDestroy, DoCheck {
  /** FormControl for managing search input state */
  readonly _searchControl = new FormControl<string>('');

  /** Writable signal tracking FormControl value — always in sync, even with emitEvent:false */
  private readonly _searchControlValue = signal<string>('');

  private readonly destroyRef = inject(DestroyRef);
  private readonly overlay = inject(Overlay);
  private readonly viewContainerRef = inject(ViewContainerRef);

  private overlayRef: OverlayRef | null = null;

  /** Tracks the previous touched state of external control. Used in DoCheck to detect programmatic touch changes. */
  private _previousExternalTouched = false;

  /** Signal to track touched state for reactive updates in computed signals */
  private readonly _isTouched = signal<boolean>(false);

  private readonly searchInputContainer = viewChild<ElementRef>('searchInputContainer');
  private readonly dropdownTemplate = viewChild<TemplateRef<any>>('dropdownPortal');

  /** Computed signal to check if search field has validation error (regardless of touched state) */
  readonly searchFieldHasError = computed(() => {
    return this.validationError() || this._searchControl.errors ? true : false;
  });

  /** Computed signal to get search error message */
  readonly searchErrorMessageDisplay = computed(() => {
    // Return custom error message if validation error exists, otherwise return the customizable error message
    if (this.validationError()) {
      return this.customErrorMessage() || this.validationError() || '';
    }
    return this.customErrorMessage() || this.searchErrorMessage();
  });

  /** Computed signal that reactively reads FormControl value */
  readonly searchInputValue = computed(() => {
    return this._searchControlValue() ?? '';
  });

  // ============================================================================
  // Inputs (Signals)
  // ============================================================================

  /** List of searchable items - required */
  readonly items = input.required<SearchItem[]>();

  /** Label for the search input field */
  readonly label = input<string>('');

  /** Minimum characters before search activates */
  readonly minChars = input<number>(1);

  /** Placeholder text for search input */
  readonly placeholder = input<string>('Search...');

  /** Custom message when search yields no results */
  readonly noResultsMessage = input<string>('No matching items found');

  /** Custom message when item list is empty */
  readonly noItemsMessage = input<string>('No items available');

  /** Number of tags to show before "+N more" indicator */
  readonly expandedCount = input<number | string>(3);

  /** Get numeric count from expandedCount (handles both number and string inputs) */
  private getNumericCount(): number | 'all' {
    const value = this.expandedCount();
    const strValue = String(value).toLowerCase().trim();

    if (strValue === 'all') {
      return 'all';
    }

    const numValue = typeof value === 'number' ? value : parseInt(strValue, 10);
    return isNaN(numValue) ? 3 : numValue; // Fallback to default
  }

  /** Whether to display selections as removable chips below the input */
  readonly showTags = input<boolean>(true);

  /** Whether multiple items can be selected */
  readonly multiple = input<boolean>(true);

  readonly allowMultipleAdds = input<boolean>(true);

  readonly allowFreeText = input<boolean>(false);

  /** Whether at least one selection is required */
  readonly required = input<boolean>(false);

  /** Custom validation error message when required field is empty */
  readonly requiredErrorMessage = input<string>('At least one selection is required');

  /** Show a required-selection error immediately instead of waiting for the control to be touched. */
  readonly showRequiredErrorImmediately = input<boolean>(false);

  /** Custom error message for search validation */
  readonly searchErrorMessage = input<string>('Search is required');

  /** Custom validation error message for required field or search validation */
  readonly customErrorMessage = input<string>('');

  /** Item height for virtual scrolling */
  readonly itemHeight = input<number>(40);
  readonly position = input<HxSelectPosition>('bottom-start');

  /** Whether external search is in progress (API mode) */
  readonly searching = input<boolean>(false);

  /**
   * When true, the component skips the local displayText substring filter on items. Use this when items are already
   * pre-filtered by an external source (e.g. IndexedDB / API) to prevent valid matches from being hidden because their
   * displayText uses a different representation (e.g. generic drug name) than the user's search query.
   */
  readonly externalSearch = input<boolean>(false);

  /** Enables near-bottom incremental loading via loadMore output. */
  readonly enableIncrementalLoading = input<boolean>(false);

  /** CSS class(es) added to the CDK overlay pane for targeted styling (e.g. max-height overrides). */
  readonly panelClass = input<string>('');

  /** Whether more results are available for current query. */
  readonly hasMore = input<boolean>(false);

  /** Whether parent is currently fetching the next page. */
  readonly loadingMore = input<boolean>(false);

  /** Bottom-distance threshold in px to trigger loadMore. */
  readonly loadMoreThresholdPx = input<number>(240);

  /** Page size hint emitted with loadMore. */
  readonly pageSize = input<number>(10);

  // ============================================================================
  // Outputs (Events)
  // ============================================================================

  /** Emitted when selected items change */
  readonly selectedItemsChanged = output<SearchItem[]>();

  /** Emitted when items array changes with updated isSelected flags */
  readonly itemsChanged = output<SearchItem[]>();

  /** Emitted when search query changes (debounced) - for external data fetching */
  readonly searchChange = output<string>();

  /** Emitted when dropdown scroll reaches near-bottom in incremental loading mode. */
  readonly loadMore = output<SearchTypeaheadLoadMoreEvent>();

  // ============================================================================
  // Internal State (Signals)
  // ============================================================================

  /** Current search query */
  readonly searchQuery = signal<string>('');

  /** Index of highlighted item during keyboard navigation */
  readonly highlightedIndex = signal<number>(-1);

  /** Whether dropdown is open */
  readonly isDropdownOpen = signal<boolean>(false);

  /** Whether all tags are expanded (vs "+N more" mode) */
  readonly tagsExpanded = signal<boolean>(false);

  readonly localSelectedItems = signal<SearchItem[]>([]);

  /** Validation error message */
  readonly validationError = signal<string | null>(null);

  readonly showError = computed(() => {
    // Never show error when items are already selected — they satisfy the required constraint.
    if (this.localSelectedItems().length > 0) return false;
    // Never show error when the user has text in the input (they are typing or have typed a value).
    if (this._searchControlValue().trim().length > 0) return false;
    const hasError = this.searchFieldHasError();
    if (!hasError) return false;
    return this.showRequiredErrorImmediately() || this._isTouched();
  });

  /** Whether a search has been performed (for API mode) */
  readonly hasSearched = signal<boolean>(false);

  /** Whether search is pending (during debounce, before API call) */
  private readonly pendingSearch = signal<boolean>(false);

  /** Combined searching state (parent's searching input OR local pending search) */
  readonly isSearching = computed(() => this.searching() || this.pendingSearch());

  /** Show a chevron-down affordance when minChars=0 and not already showing a start icon (tags mode). */
  readonly showDropdownChevron = computed(() => !this.showTags() && this.minChars() === 0);

  /**
   * Whether the inner HxInput should be marked required. When tags are used, required is satisfied by having ≥1
   * selected item, not by input text.
   */
  readonly requiresInputValue = computed(() => {
    if (!this.required()) return false;
    // Any selection (dropdown pick or free-text) satisfies the required constraint.
    if (this.localSelectedItems().length > 0) return false;
    return true;
  });

  // ============================================================================
  // Computed Properties
  // ============================================================================

  readonly displayValue = computed(() => {
    // Use FormControl value as the source of truth for display
    return this._searchControl.value || '';
  });

  /** Filtered items based on search query. Includes text highlighting for matched text. */
  readonly filteredItems = computed(() => {
    const query = this.searchQuery().toLowerCase();
    if (query.length < this.minChars()) return [];

    const selectedIds = new Set(this.localSelectedItems().map((item) => this.getItemIdentity(item)));
    const skipFilter = this.externalSearch();
    const isCheckboxMulti = this.multiple() && this.showTags() && this.allowMultipleAdds();

    return this.items()
      .filter((item) => {
        if (!isCheckboxMulti && selectedIds.has(this.getItemIdentity(item))) return false;
        if (skipFilter) return true;
        return item.displayText.toLowerCase().includes(query);
      })
      .map((item) => ({
        ...item,
        highlightedLabel: this.highlightMatches(item.displayText, query),
        secondaryLabel: this.getSecondaryLabel(item),
      }));
  });

  /** Tags to display (respecting expandedCount limit) */
  readonly visibleTags = computed(() => {
    const selected = this.localSelectedItems();
    const expanded = this.tagsExpanded();
    const count = this.getNumericCount();

    // Show all tags if count is 'all' or tags are manually expanded
    if (count === 'all' || expanded) {
      return selected;
    }

    if (selected.length <= count) {
      return selected;
    }
    return selected.slice(0, count);
  });

  /** Count of hidden tags */
  readonly hiddenTagCount = computed(() => {
    const selected = this.localSelectedItems();
    const expanded = this.tagsExpanded();
    const count = this.getNumericCount();

    // No hidden tags if count is 'all' or tags are manually expanded
    if (count === 'all' || expanded) {
      return 0;
    }

    if (selected.length <= count) {
      return 0;
    }
    return selected.length - count;
  });

  /** Whether there are matching search results */
  readonly hasResults = computed(() => {
    const query = this.searchQuery();
    const minCharsThreshold = this.minChars();
    return query.length >= minCharsThreshold && this.filteredItems().length > 0;
  });

  /** Whether to show "no results" message */
  readonly showNoResults = computed(() => {
    const query = this.searchQuery();
    const minCharsThreshold = this.minChars();

    if (this.allowFreeText()) {
      return false;
    }

    // Don't show if currently searching (including pending debounce)
    if (this.isSearching()) {
      return false;
    }

    // Show "no results" when:
    // 1. Dropdown is open and query meets min chars
    // 2. No filtered results
    // 3. Items array HAS data (static mode) OR user searched (query is non-empty)
    return (
      this.isDropdownOpen() &&
      query.length >= minCharsThreshold &&
      this.filteredItems().length === 0 &&
      (this.items().length > 0 || query.trim().length > 0)
    );
  });

  /** Whether to show "no items available" message */
  readonly showNoItems = computed(() => {
    // Don't show "no items" when searching (including pending) or before first search
    if (this.isSearching() || !this.hasSearched()) {
      return false;
    }
    // Show when items array is empty AND search query is empty
    return this.isDropdownOpen() && this.items().length === 0 && this.searchQuery().trim().length === 0;
  });

  /** Whether dropdown should be visible */
  readonly dropdownVisible = computed(() => {
    return (
      this.isDropdownOpen() &&
      (this.isSearching() || this.filteredItems().length > 0 || this.showNoResults() || this.showNoItems())
    );
  });

  // ============================================================================
  // Private Properties
  // ============================================================================

  private readonly destroy$ = new Subject<void>();
  private readonly searchQuery$ = new Subject<string>();
  private lastLoadMoreEmitAt = 0;

  constructor() {
    super();

    // Intercept FormControl.setValue so _searchControlValue signal stays in sync
    // even when setValue is called with { emitEvent: false }
    const originalSetValue = this._searchControl.setValue.bind(this._searchControl);
    this._searchControl.setValue = (value: any, options?: any) => {
      originalSetValue(value, options);
      this._searchControlValue.set(value ?? '');
    };

    if (this.ngControl) {
      // Manually register this instance as the value accessor and validator
      // This breaks the DI cycle caused by the NG_VALUE_ACCESSOR token lookup.
      this.ngControl.valueAccessor = this;
    }

    // NEW: Effect to sync localSelectedItems when items arrive asynchronously
    effect(() => {
      const currentItems = this.items();
      const currentVal = this.ngControl?.control?.value;

      // Mark that a search has been performed when items change
      // (items are updated by parent after API call)
      if (currentItems.length >= 0 && this.searchQuery().length > 0) {
        this.hasSearched.set(true);
      }

      // Re-apply the saved form-control value only when there is no active selection and no
      // active search query. This covers the edit-mode initialisation case (items load after the
      // saved value is written) without re-selecting a previously chosen item when the user has
      // cleared the input and started a new search.
      // untracked prevents localSelectedItems from becoming a reactive dependency (avoids loop).
      const hasNonEmptyValue =
        currentVal &&
        !(Array.isArray(currentVal) && currentVal.length === 0) &&
        !(typeof currentVal === 'string' && !currentVal.trim());
      if (
        currentItems.length > 0 &&
        hasNonEmptyValue &&
        untracked(() => this.localSelectedItems()).length === 0 &&
        !this.searchQuery()
      ) {
        this._setValue(currentVal);
      }
    });

    // NEW: Explicitly sync the _searchControl (the input) when local selection changes and tags are hidden
    effect(() => {
      if (!this.showTags()) {
        const selected = this.localSelectedItems();
        if (selected.length > 0) {
          // Display first item (or join if multiple)
          const text = this.multiple()
            ? selected.map((i) => i.displayText).join(', ')
            : (selected[0]?.displayText ?? '');

          if (this._searchControl.value !== text) {
            this._searchControl.setValue(text, { emitEvent: false });
            this.searchQuery.set(text);
          }
        }
        // Do NOT clear the input here — the user may be actively typing.
        // Explicit resets (_setValue(''), clearSearch) already clear _searchControl directly.
      }
    });
    // Initialize selected items from items with isSelected=true ONLY if no FormControl value exists
    effect(() => {
      const itemsWithIsSelected = this.items().filter((item) => item.isSelected === true);

      // Only set initial values if localSelectedItems is empty (no FormControl value set)
      if (this.localSelectedItems().length === 0 && itemsWithIsSelected.length > 0) {
        this.localSelectedItems.set(itemsWithIsSelected);

        if (!this.showTags() && itemsWithIsSelected.length > 0) {
          // If showTags is false, display selected items in the input
          if (this.multiple()) {
            // If multiple is true, show as comma-separated
            const displayText = itemsWithIsSelected.map((item) => item.displayText).join(', ');
            this.searchQuery.set(displayText);
            this._searchControl.setValue(displayText);
          } else {
            // If multiple is false, show only the first item
            this.searchQuery.set(itemsWithIsSelected[0]?.displayText || '');
            this._searchControl.setValue(itemsWithIsSelected[0]?.displayText || '');
          }
        }
      }
    });

    // Close dropdown when allowFreeText is enabled and there are no filtered results.
    // Use isSearching() (not just pendingSearch) so external search loading keeps the dropdown open.
    effect(() => {
      const hasResults = this.filteredItems().length > 0;
      const query = this.searchQuery();
      const minChars = this.minChars();
      const hasItems = this.items().length > 0;
      const isSearching = this.isSearching();
      if (
        !isSearching &&
        ((this.allowFreeText() && query.length >= minChars && !hasResults) ||
          (!hasItems && query.length >= minChars && this.allowFreeText() && !hasResults))
      ) {
        this.closeDropdown();
      }
    });

    // Validate required field
    effect(() => {
      const isRequired = this.required();
      const selectedItems = this.localSelectedItems();

      if (isRequired && selectedItems.length === 0) {
        this.validationError.set(this.requiredErrorMessage());
      } else {
        this.validationError.set(null);
      }
    });

    // Clean up overlay on destroy
    this.destroyRef.onDestroy(() => {
      this.overlayRef?.dispose();
    });
  }

  // ============================================================================
  // Lifecycle
  // ============================================================================

  ngOnInit(): void {
    // CRITICAL: Initialize ngControl from parent class for validation state detection
    super.ngOnInit();

    // Setup Alt+K keyboard shortcut for focus
    this.setupGlobalKeyboardShortcut();

    // Setup search debouncing
    this.searchQuery$
      .pipe(
        debounceTime(300),
        map((query) => query.trim()),
        takeUntil(this.destroy$)
      )
      .subscribe((query) => {
        this.searchQuery.set(query);
        this.highlightedIndex.set(-1);
        this.lastLoadMoreEmitAt = 0;

        // Emit for external data fetching (e.g., IndexedDB search)
        this.searchChange.emit(query);

        // Clear pending state - debounce complete
        this.pendingSearch.set(false);
      });
  }

  /** Handle dropdown scroll and request next page when user reaches near-bottom. */
  onDropdownScroll(event: Event): void {
    if (!this.enableIncrementalLoading()) return;
    if (this.loadingMore() || !this.hasMore()) return;

    const target = event.target as HTMLElement | null;
    if (!target) return;

    const distanceToBottom = target.scrollHeight - (target.scrollTop + target.clientHeight);
    if (distanceToBottom > this.loadMoreThresholdPx()) return;

    // Guard against rapid duplicate emits while parent flips loadingMore.
    const now = Date.now();
    if (now - this.lastLoadMoreEmitAt < 150) return;
    this.lastLoadMoreEmitAt = now;

    this.loadMore.emit({
      query: this.searchQuery(),
      pageSize: this.pageSize(),
    });
  }

  /**
   * DoCheck lifecycle hook to detect programmatic touch state changes. This is critical for handling
   * form.markAllAsTouched() which doesn't trigger statusChanges. When the external control is touched, we propagate
   * that to the internal _searchControl so that the HxInput displays the validation error inline.
   */
  ngDoCheck(): void {
    const externalControl = this.ngControl?.control;
    if (externalControl) {
      const currentTouched = externalControl.touched;
      // If touched state changed externally (e.g., form.markAllAsTouched())
      if (currentTouched && currentTouched !== this._previousExternalTouched) {
        this._previousExternalTouched = currentTouched;
        // Propagate touched state to internal _searchControl so HxInput shows errors
        this._searchControl.markAsTouched();
        // Update signal for reactive computed updates
        this._isTouched.set(true);
      }
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ============================================================================
  // Overlay Management
  // ============================================================================

  /** Open the dropdown using CDK Overlay */
  private openDropdown(): void {
    if (this.disabledState() || this.isDropdownOpen()) {
      return;
    }

    const trigger = this.searchInputContainer();
    const template = this.dropdownTemplate();

    if (!trigger || !template) {
      return;
    }

    // Create overlay if it doesn't exist
    if (!this.overlayRef) {
      const positionStrategy = this.createPositionStrategy();

      this.overlayRef = this.overlay.create({
        positionStrategy,
        // Reposition keeps the panel anchored to the trigger as the parent scrolls.
        scrollStrategy: this.overlay.scrollStrategies.reposition({ scrollThrottle: 20 }),
        hasBackdrop: true,
        backdropClass: 'hx-search-typeahead-backdrop',
        disposeOnNavigation: true,
        width: trigger.nativeElement?.offsetWidth,
        minWidth: trigger.nativeElement?.offsetWidth,
        panelClass: this.panelClass() || undefined,
      });

      this.overlayRef.backdropClick().subscribe(() => {
        const query = this.searchQuery().trim();
        if (this.allowFreeText() && query && (!this.showTags() || !this.allowMultipleAdds())) {
          this.submitAsFreeText(query, true);
        }
        this.closeDropdown();
        this.markTouched();
      });
    }

    // Attach the template portal
    if (this.overlayRef && !this.overlayRef.hasAttached()) {
      const portal = new TemplatePortal(template, this.viewContainerRef);
      this.overlayRef.attach(portal);
    }

    this.isDropdownOpen.set(true);
    this.highlightedIndex.set(-1);
  }

  /** Close the dropdown */
  private closeDropdown(): void {
    if (!this.isDropdownOpen()) {
      return;
    }

    this.overlayRef?.detach();
    this.isDropdownOpen.set(false);
    this.highlightedIndex.set(-1);
  }

  // ============================================================================
  // ControlValueAccessor Implementation
  // ============================================================================

  /**
   * Set value from FormControl (override from HxBaseControl)
   *
   * @param value - Array of SearchItems or null
   * @protected
   */
  protected override _setValue(value: SearchItem[] | string | null): void {
    if (typeof value === 'string') {
      const trimmedValue = value.trim();
      if (!trimmedValue) {
        this.localSelectedItems.set([]);
        if (!this.showTags()) {
          this._searchControl.setValue('', { emitEvent: false });
          this.searchQuery.set('');
        }
        return;
      }

      const matchedItem = this.items().find((item) => item.displayText === trimmedValue);
      if (matchedItem) {
        this.localSelectedItems.set([matchedItem]);
      } else if (this.allowFreeText()) {
        this.localSelectedItems.set([
          {
            id: crypto.randomUUID(),
            displayText: trimmedValue,
            isFreetext: true,
          },
        ]);
      } else {
        this.localSelectedItems.set([]);
      }

      if (!this.showTags()) {
        this._searchControl.setValue(trimmedValue, { emitEvent: false });
        this.searchQuery.set(trimmedValue);
      }
      return;
    }

    if (!value || (!Array.isArray(value) && typeof value !== 'object')) {
      this.localSelectedItems.set([]);
      if (!this.showTags()) {
        this._searchControl.setValue('', { emitEvent: false });
        this.searchQuery.set('');
      }
      return;
    }

    // ponytail: normalize single object or array value formats
    const valueArray = Array.isArray(value) ? value : [value];
    // Map string entries (e.g. from patchValue in edit mode) to matching SearchItems by displayText.
    const normalizedItems: SearchItem[] = valueArray.map((v: any) => {
      if (typeof v === 'string') {
        const target = v.toLowerCase().trim();
        const found = this.items().find((item) => {
          return (
            item.displayText?.toLowerCase().trim() === target ||
            (item.metadata?.['value'] as string)?.toLowerCase().trim() === target
          );
        });
        return found ?? { id: v, displayText: v, isFreetext: true };
      }
      return v as SearchItem;
    });
    this.localSelectedItems.set(normalizedItems);

    // Update search input display if showTags is false
    if (!this.showTags() && valueArray.length > 0) {
      if (this.multiple()) {
        const displayText = valueArray.map((item) => item.displayText).join(', ');
        this._searchControl.setValue(displayText, { emitEvent: false });
        this.searchQuery.set(displayText);
      } else {
        this._searchControl.setValue(valueArray[0]?.displayText || '', { emitEvent: false });
        this.searchQuery.set(valueArray[0]?.displayText || '');
      }
    }
  }

  // ============================================================================
  // Public Methods
  // ============================================================================

  /** Handle selection of an item from dropdown */
  selectItem(item: SearchItem): void {
    if (this.disabledState()) return;

    // 1. Update selection state
    const itemIdentity = this.getItemIdentity(item);
    const isAlreadySelected = this.localSelectedItems().some(
      (selected) => this.getItemIdentity(selected) === itemIdentity
    );

    if (!this.multiple() || !this.showTags() || !this.allowMultipleAdds()) {
      this.localSelectedItems.set([item]);
    } else if (isAlreadySelected) {
      // Toggle off — deselect the item
      this.localSelectedItems.update((current) =>
        current.filter((selected) => this.getItemIdentity(selected) !== itemIdentity)
      );
    } else {
      this.localSelectedItems.update((current) => [...current, item]);
    }

    // 2. Emit events
    this.emitSelectedItems();
    this.emitItemsWithSelection();

    if (this.allowMultipleAdds()) {
      if (this.showTags()) {
        // Keep the dropdown open so the user can select/deselect multiple items.
        // Do NOT clear the search query — the user may be filtering the list.
      }
      this.searchInputContainer()?.nativeElement?.focus();
      this.highlightedIndex.set(-1);
    } else {
      // When allowMultipleAdds is false and showTags is false, update input with selected item
      if (!this.showTags()) {
        this.searchQuery.set(item.displayText);
        this._searchControl.setValue(item.displayText);
      } else {
        this.searchQuery.set('');
        this._searchControl.setValue('');
      }
      this.closeDropdown();
    }
  }

  /** Open dropdown on focus when minChars=0 (combobox behaviour). */
  onInputFocus(): void {
    if (this.disabledState() || this.minChars() !== 0 || this.isDropdownOpen()) return;
    // Reset the filter so all options are visible (searchQuery may hold the previously selected text).
    this.searchQuery.set('');
    this.openDropdown();
  }

  /** Open dropdown on mousedown on non-input parts (e.g. chevron end-icon) without triggering blur. */
  onInputMouseDown(event: MouseEvent): void {
    if (this.disabledState() || this.minChars() !== 0) return;
    const inputEl = this.searchInputContainer()?.nativeElement?.querySelector('input');
    if (event.target !== inputEl) {
      event.preventDefault();
      if (!this.isDropdownOpen()) {
        // Reset filter so the full options list is shown.
        this.searchQuery.set('');
        this.openDropdown();
      }
      inputEl?.focus();
    }
  }

  /** Returns true when the given item is in the current selection (for checkbox display). */
  isItemSelected(item: SearchItem): boolean {
    const itemIdentity = this.getItemIdentity(item);
    return this.localSelectedItems().some((selected) => this.getItemIdentity(selected) === itemIdentity);
  }

  getItemIdentity(item: SearchItem): string {
    const metadata = item.metadata;
    const code = metadata?.['code'] ?? metadata?.['icdCode'] ?? metadata?.['ICDCode'];
    return String(code ?? item.id)
      .trim()
      .toLowerCase();
  }

  onSearchInput(query: string): void {
    if (this.disabledState()) return;

    // Update FormControl
    this._searchControl.setValue(query);
    this._searchControl.markAsDirty();

    // When showTags is false, clear selection when user starts editing the input
    if (!this.showTags() && this.localSelectedItems().length > 0) {
      this.localSelectedItems.set([]);
      this.emitItemsWithSelection();
    }
    // For local (non-external) single-value fields keep the form control updated with the typed
    // text so Validators.required passes while the user is typing.
    if (!this.showTags() && !this.externalSearch()) {
      this.notifyChange(query);
    }

    // Close dropdown if query is empty
    if (!query || query.trim() === '') {
      this.closeDropdown();
      this.searchQuery.set('');
      this.searchQuery$.next('');
      this.pendingSearch.set(false);
      return;
    }

    // Set pending state to show "Searching..." during debounce
    this.pendingSearch.set(true);
    this.searchQuery$.next(query);

    // Open dropdown if we meet the minChars threshold
    if (query.length >= this.minChars()) {
      this.openDropdown();
    }
  }

  clearSearch(): void {
    if (this.disabledState()) return;

    this.searchQuery.set('');
    this.searchQuery$.next('');
    this._searchControl.setValue('');
    this._searchControl.markAsUntouched();
    this.pendingSearch.set(false);
    this.closeDropdown();

    if (!this.showTags()) {
      this.localSelectedItems.set([]);
      this.emitSelectedItems();
      this.emitItemsWithSelection();
    }

    this.searchInputContainer()?.nativeElement?.focus();
  }

  closeSearchSession(): void {
    this.searchQuery.set('');
    this.pendingSearch.set(false);
    this.closeDropdown();
  }

  /** Handle removal of a tag */
  removeTag(item: SearchItem): void {
    if (this.disabledState()) return;

    // Prevent removal of locked items (injected from other controls)
    if (item.locked) return;

    const itemIdentity = this.getItemIdentity(item);
    this.localSelectedItems.update((items) =>
      items.filter((selected) => this.getItemIdentity(selected) !== itemIdentity)
    );

    this.emitSelectedItems();
    this.emitItemsWithSelection();
  }

  /** Toggle tag expansion (show all vs "+N more") */
  toggleTagExpansion(): void {
    if (this.disabledState()) return;
    this.tagsExpanded.update((expanded) => !expanded);
  }

  /** Handle keyboard navigation in dropdown */
  onKeyDown(event: KeyboardEvent): void {
    if (this.disabledState()) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.openDropdown();
        this.moveHighlight(1);
        break;

      case 'ArrowUp':
        event.preventDefault();
        this.moveHighlight(-1);
        break;

      case 'Enter':
        event.preventDefault();
        if (this.isDropdownOpen() && this.highlightedIndex() >= 0) {
          const item = this.filteredItems()[this.highlightedIndex()];
          if (item) {
            this.selectItem(item);
          }
        } else {
          const query = this.searchQuery().trim();
          if (this.allowFreeText() && query && this.showTags()) {
            this.submitAsFreeText(query, true);
            this.closeDropdown();
          }
        }
        break;

      case 'Escape':
        event.preventDefault();
        this.closeDropdown();
        this.searchInputContainer()?.nativeElement?.focus();
        break;

      case 'Tab':
        this.closeDropdown();
        break;

      case 'Backspace':
        if (!this.showTags() && this.searchQuery() === '') {
          if (this.localSelectedItems().length > 0) {
            this.localSelectedItems.set([]);
            this.emitSelectedItems();
            this.emitItemsWithSelection();
          }
        }
        break;
    }
  }

  private submitAsFreeText(text: string, clearQuery: boolean = false): void {
    const exists = this.localSelectedItems().some((s) => s.displayText === text);
    if (exists && this.multiple()) return;

    const freeTextItem: SearchItem = {
      id: crypto.randomUUID(),
      displayText: text,
      isFreetext: true,
    };

    if (!this.multiple() || !this.showTags()) {
      this.localSelectedItems.set([freeTextItem]);
    } else {
      this.localSelectedItems.update((current) => [...current, freeTextItem]);
    }

    this.emitSelectedItems();
    this.emitItemsWithSelection();

    if (clearQuery) {
      this.searchQuery.set('');
      this._searchControl.setValue('', { emitEvent: false });
      this.searchQuery$.next('');
      this.pendingSearch.set(false);
    }
  }

  /** Handle blur to close dropdown */
  onBlur(event: FocusEvent): void {
    const relatedTarget = event.relatedTarget as HTMLElement;

    // Check if focus is moving to a result item
    const isResultItem = relatedTarget?.classList.contains('hx-search-typeahead__result-item');

    // Check if focus is moving to dropdown overlay
    const isDropdownElement = relatedTarget?.closest('.hx-search-typeahead__dropdown');

    // Only close if NOT clicking on dropdown items
    if (!isResultItem && !isDropdownElement) {
      // Delay closing to allow click events on dropdown items to register
      setTimeout(() => {
        const query = this.searchQuery().trim();
        if (this.allowFreeText() && query) {
          this.submitAsFreeText(query, true);
        }
        this.closeDropdown();

        // Mark control as touched (ControlValueAccessor)
        this.markTouched();
        // Update signal for reactive computed updates
        this._isTouched.set(true);
      }, 150);
    }
  }

  // ============================================================================
  // Private Methods
  // ============================================================================

  /** Emit items array with updated isSelected flags */
  private emitItemsWithSelection(): void {
    const selectedIds = new Set(this.localSelectedItems().map((item) => this.getItemIdentity(item)));

    const updatedItems = this.items().map((item) => ({
      ...item,
      isSelected: selectedIds.has(this.getItemIdentity(item)),
    }));
    this.itemsChanged.emit(updatedItems);
  }

  /** Emit selected items array */
  private emitSelectedItems(): void {
    const selectedItems = this.localSelectedItems();
    this.selectedItemsChanged.emit(selectedItems);

    // Notify FormControl of value change (ControlValueAccessor)
    this.notifyChange(selectedItems);
  }

  /** Move highlight to next/previous item during keyboard navigation */
  private moveHighlight(direction: 1 | -1): void {
    const items = this.filteredItems();
    if (items.length === 0) return;

    let newIndex = this.highlightedIndex() + direction;

    // Loop around
    if (newIndex < 0) {
      newIndex = items.length - 1;
    } else if (newIndex >= items.length) {
      newIndex = 0;
    }

    this.highlightedIndex.set(newIndex);

    const item = items[newIndex];
    if (item) {
      const announcement = `Result ${newIndex + 1} of ${items.length}: ${item.displayText}`;
      // this.liveAnnouncer.announce(announcement);
    }
  }

  /** Highlight matched text in item display text */
  private highlightMatches(text: string, query: string): string {
    if (!query) return text;

    const regex = new RegExp(`(${this.escapeRegex(query)})`, 'gi');
    return text.replace(regex, '<mark>$1</mark>');
  }

  /** Return a secondary label for search results when one is provided in item metadata. */
  private getSecondaryLabel(item: SearchItem): string {
    const description = item.metadata?.['description'];
    if (typeof description !== 'string') {
      return '';
    }

    const normalizedDescription = description.trim();
    if (!normalizedDescription) {
      return '';
    }

    // Prevent duplicate lines when primary label already includes full description text.
    if (item.displayText.toLowerCase().includes(normalizedDescription.toLowerCase())) {
      return '';
    }

    return normalizedDescription;
  }

  /** Escape special regex characters */
  private escapeRegex(str: string): string {
    return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  /** Setup Alt+K global keyboard shortcut */
  private setupGlobalKeyboardShortcut(): void {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.altKey && event.key === 'k') {
        event.preventDefault();
        this.searchInputContainer()?.nativeElement?.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    this.destroy$.subscribe(() => {
      document.removeEventListener('keydown', handleKeyDown);
    });
  }

  private createPositionStrategy(): FlexibleConnectedPositionStrategy {
    const trigger = this.searchInputContainer();
    if (!trigger) {
      throw new Error('Trigger element not found');
    }

    const positions = this.getPositions();

    return this.overlay
      .position()
      .flexibleConnectedTo(trigger?.nativeElement)
      .withPositions(positions)
      .withFlexibleDimensions(false)
      .withPush(false)
      .withViewportMargin(8);
  }

  /** Get CDK positions based on position input */
  private getPositions(): ConnectedPosition[] {
    const positionMap: Record<HxSelectPosition, ConnectedPosition[]> = {
      'bottom-start': [
        { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 4 },
        { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -4 },
      ],
      bottom: [
        { originX: 'center', originY: 'bottom', overlayX: 'center', overlayY: 'top', offsetY: 4 },
        { originX: 'center', originY: 'top', overlayX: 'center', overlayY: 'bottom', offsetY: -4 },
      ],
      'bottom-end': [
        { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: 4 },
        { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom', offsetY: -4 },
      ],
      'top-start': [
        { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -4 },
        { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 4 },
      ],
      top: [
        { originX: 'center', originY: 'top', overlayX: 'center', overlayY: 'bottom', offsetY: -4 },
        { originX: 'center', originY: 'bottom', overlayX: 'center', overlayY: 'top', offsetY: 4 },
      ],
      'top-end': [
        { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom', offsetY: -4 },
        { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: 4 },
      ],
      auto: [
        { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 4 },
        { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -4 },
        { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: 4 },
        { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom', offsetY: -4 },
      ],
      left: [{ originX: 'start', originY: 'center', overlayX: 'end', overlayY: 'center', offsetX: -4 }],
      'left-start': [{ originX: 'start', originY: 'top', overlayX: 'end', overlayY: 'top', offsetX: -4 }],
      'left-end': [{ originX: 'start', originY: 'bottom', overlayX: 'end', overlayY: 'bottom', offsetX: -4 }],
      right: [{ originX: 'end', originY: 'center', overlayX: 'start', overlayY: 'center', offsetX: 4 }],
      'right-start': [{ originX: 'end', originY: 'top', overlayX: 'start', overlayY: 'top', offsetX: 4 }],
      'right-end': [{ originX: 'end', originY: 'bottom', overlayX: 'start', overlayY: 'bottom', offsetX: 4 }],
    };

    return positionMap[this.position()] || positionMap['bottom-start'];
  }
}
